import { Button, Drawer, message, Typography } from "antd";
import { useCallback, useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import { CARTON_WAREHOUSE_LIST_ENDPOINT } from "../../../apiServices/API_ENDPOINTS";
import { getData } from "../../../apiServices/common";
import { getMovementType, getMovementTypeLabel } from "../../../common/helper/helper";
import { isArrayAndHasValue } from "../../../utils/functions";
import LocationAssignForm from "./LocationAssignForm";
import "./index.css";
import "../../InventoryTrims/ItemTable/index.css";

const ItemAssignDrawer = ({ open, onClose, selectedRows, movement, tableType }) => {
  const [checkedItems, setCheckedItems] = useState([]);
  const [warehouseList, setWarehouseList] = useState([]);

  const movementCategory = getMovementType(movement);
  const drawerTitle = getMovementTypeLabel(movement) || "Assign Items";

  const getAllWarehouse = useCallback(async (filterValues = {}) => {
    const response = await getData(CARTON_WAREHOUSE_LIST_ENDPOINT, false, {
      page: 1,
      per_page: 100,
      ...(filterValues?.name && { name: filterValues.name }),
    });
    if (response?.status === 200 || response?.data?.code === 200) {
      setWarehouseList(response?.data?.data?.data || []);
    } else {
      message.error("Error getting carton warehouse data");
    }
  }, []);

  useEffect(() => {
    if (open) {
      getAllWarehouse();
      const mapped = (selectedRows || []).map((row) => ({
        unique_id: uuidv4(),
        source_id: row.id,
        table_type: tableType,
        item_type_name: row.item_type_name || row.item_category?.name || row.material_type_name || "—",
        item_name:
          row.item_name ||
          row.style_description ||
          row.carton_type?.name ||
          `Material #${row.material_type_id || row.id}`,
        balance_quantity: row.balance_quantity ?? row.carton_total_qty ?? row.material_qty ?? 0,
        warehouse_id: null,
        floor_id: null,
        partition_id: null,
        floorsList: [],
        partitionsList: [],
        req_qty: null,
      }));
      setCheckedItems(mapped);
    } else {
      setCheckedItems([]);
    }
  }, [open, selectedRows, tableType, getAllWarehouse]);

  const onWarehouseSelect = (selectedWarehouseId, rowId) => {
    const filteredWarehouse = warehouseList?.find((item) => Number(item.id) === Number(selectedWarehouseId));
    const floorsList = isArrayAndHasValue(filteredWarehouse?.floors_info) ? filteredWarehouse.floors_info : [];

    setCheckedItems((prev) =>
      prev.map((item) =>
        item.unique_id === rowId
          ? {
              ...item,
              warehouse_id: selectedWarehouseId,
              floorsList,
              floor_id: null,
              partition_id: null,
              partitionsList: [],
            }
          : item,
      ),
    );
  };

  const onFloorSelect = (selectedFloorId, rowId) => {
    setCheckedItems((prev) =>
      prev.map((item) => {
        if (item.unique_id !== rowId) return item;
        const filteredFloor = item.floorsList?.find((f) => Number(f.id) === Number(selectedFloorId));
        const partitionsList = isArrayAndHasValue(filteredFloor?.partitions_info)
          ? filteredFloor.partitions_info
          : [];
        return { ...item, floor_id: selectedFloorId, partitionsList, partition_id: null };
      }),
    );
  };

  const onPartitionSelect = (selectedPartitionId, rowId) => {
    setCheckedItems((prev) =>
      prev.map((item) => (item.unique_id === rowId ? { ...item, partition_id: selectedPartitionId } : item)),
    );
  };

  const onChangeQuantity = (value, rowId) => {
    setCheckedItems((prev) =>
      prev.map((item) => (item.unique_id === rowId ? { ...item, req_qty: value } : item)),
    );
  };

  const copyToNextRows = (rowId) => {
    const source = checkedItems.find((item) => item.unique_id === rowId);
    if (!source) return;
    const idx = checkedItems.findIndex((item) => item.unique_id === rowId);
    setCheckedItems((prev) =>
      prev.map((item, i) =>
        i > idx
          ? {
              ...item,
              warehouse_id: source.warehouse_id,
              floor_id: source.floor_id,
              partition_id: source.partition_id,
              floorsList: source.floorsList,
              partitionsList: source.partitionsList,
              req_qty: source.req_qty,
            }
          : item,
      ),
    );
  };

  const onCopyRow = (product) => {
    setCheckedItems((prev) => [
      ...prev,
      {
        ...product,
        unique_id: uuidv4(),
        is_add: true,
      },
    ]);
  };

  const handleSubmit = () => {
    message.info("Save functionality will be implemented with backend integration.");
    onClose();
  };

  return (
    <Drawer
      title={drawerTitle}
      placement="right"
      width={1100}
      onClose={onClose}
      open={open}
      destroyOnClose
      footer={
        <div style={{ textAlign: "right" }}>
          <Button onClick={onClose} style={{ marginRight: 8 }}>
            Cancel
          </Button>
          <Button type="primary" onClick={handleSubmit}>
            Save
          </Button>
        </div>
      }
    >
      <Typography.Title level={5}>Location and Item Details</Typography.Title>
      <LocationAssignForm
        warehouseList={warehouseList}
        getAllWarehouse={getAllWarehouse}
        onWarehouseSelect={onWarehouseSelect}
        onFloorSelect={onFloorSelect}
        onPartitionSelect={onPartitionSelect}
        onChangeQuantity={onChangeQuantity}
        checkedItems={checkedItems}
        movementType={movementCategory}
        onCopyRow={onCopyRow}
        copyToNextRows={copyToNextRows}
      />
    </Drawer>
  );
};

export default ItemAssignDrawer;
